import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useProjectHealth } from '../../hooks/useProjectHealth';
import { ProjectHealthCard } from './ProjectHealthCard';

function LegendDot({ colorClass, label }: { colorClass: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${colorClass}`} />
      {label}
    </span>
  );
}

export function ProjectHealthList() {
  const { t } = useTranslation('analytics');
  const { data, isLoading } = useProjectHealth();

  if (isLoading) {
    return (
      <div className="space-y-4" data-testid="analytics-project-health-list">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full rounded-lg" />
        ))}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-44 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!data?.length) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">{t('empty.projectHealth')}</p>
    );
  }

  return (
    <div className="space-y-4" data-testid="analytics-project-health-list">
      {/* Stacked bar overview */}
      <div className="space-y-3">
        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4">
          <LegendDot colorClass="bg-blue-500" label={t('projectHealth.legend.done')} />
          <LegendDot colorClass="bg-cyan-400" label={t('projectHealth.legend.inProgress')} />
          <LegendDot colorClass="bg-gray-300" label={t('projectHealth.legend.todo')} />
          <LegendDot colorClass="bg-red-500" label={t('projectHealth.legend.overdue')} />
        </div>

        {/* Per-project stacked bars */}
        {data.map((project) => {
          const total = project.totalTasks || 1;
          const donePct = (project.doneTasks / total) * 100;
          const inProgressPct = (project.inProgressTasks / total) * 100;
          const todoPct = (project.todoTasks / total) * 100;
          const overduePct = (project.overdueTasks / total) * 100;

          return (
            <div key={project.projectId ?? project.projectName}>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-sm font-medium">{project.projectName ?? '—'}</span>
                <span className="text-xs text-muted-foreground">
                  {t('projectHealth.taskCount', { count: project.totalTasks })}
                </span>
              </div>
              <div className="flex h-3 overflow-hidden rounded-full bg-secondary">
                {donePct > 0 && (
                  <div className="h-full bg-blue-500" style={{ width: `${donePct}%` }} />
                )}
                {inProgressPct > 0 && (
                  <div className="h-full bg-cyan-400" style={{ width: `${inProgressPct}%` }} />
                )}
                {todoPct > 0 && (
                  <div className="h-full bg-gray-300" style={{ width: `${todoPct}%` }} />
                )}
                {overduePct > 0 && (
                  <div className="h-full bg-red-500" style={{ width: `${overduePct}%` }} />
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t" />

      {/* Cards grid */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {data.map((project) => (
          <ProjectHealthCard key={project.projectId ?? project.projectName} project={project} />
        ))}
      </div>
    </div>
  );
}
